import React from "react";
import { Modal, Button, Form, Nav, Tab } from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { format } from "date-fns";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { fetchData, formatConsecutiveDates } from "../../helpers";
import { InfoTooltip } from "../../components/Base";
import { RESERVATION_COLOR } from "../../constants";

const MySwal = withReactContent(Swal);

export default class BulkUpdateModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "tables", // 'tables' or 'experiences'
      selectedIds: [],

      // Table specific state
      max_reservable_days: "",

      // Shared date state (used for blockout_dates OR available_days)
      selectedDates: [],

      mode: "replace", // 'replace' | 'append'
      showCalendar: false,
      isSubmitting: false,
    };
    this.calendarRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      // Reset state when modal opens
      // Determine default tab based on available data
      const hasTables = this.props.tables && this.props.tables.length > 0;
      const hasExperiences =
        this.props.experiences && this.props.experiences.length > 0;

      let defaultTab = "tables";
      if (!hasTables && hasExperiences) defaultTab = "experiences";

      this.setState({
        activeTab: defaultTab,
        selectedIds: [],
        max_reservable_days: "",
        selectedDates: [],
        mode: "replace",
        showCalendar: false,
        isSubmitting: false,
      });
    }
  }

  handleTabChange = (k) => {
    this.setState({
      activeTab: k,
      selectedIds: [],
      selectedDates: [], // Clear dates when switching context to prevent confusion
      max_reservable_days: "",
      mode: "replace",
    });
  };

  handleCalendarEntered = () => {
    if (this.calendarRef.current) {
      this.calendarRef.current.getApi().updateSize();
    }
  };

  getItems = () => {
    const { activeTab } = this.state;
    const { tables, experiences } = this.props;
    return activeTab === "tables" ? tables || [] : experiences || [];
  };

  handleSelectAll = (e) => {
    const items = this.getItems();
    if (e.target.checked) {
      const ids = items.map((t) => t._id);
      this.setState({ selectedIds: ids });
    } else {
      this.setState({ selectedIds: [] });
    }
  };

  handleItemSelect = (id) => {
    const { selectedIds } = this.state;
    if (selectedIds.includes(id)) {
      this.setState({
        selectedIds: selectedIds.filter((existingId) => existingId !== id),
      });
    } else {
      this.setState({ selectedIds: [...selectedIds, id] });
    }
  };

  handleDateClick = (info) => {
    const dateStr = info.dateStr;
    const { selectedDates } = this.state;
    if (selectedDates.includes(dateStr)) {
      this.setState({
        selectedDates: selectedDates.filter((d) => d !== dateStr),
      });
    } else {
      this.setState({ selectedDates: [...selectedDates, dateStr] });
    }
  };

  dayCellClassNames = (arg) => {
    const dateStr = format(arg.date, "yyyy-MM-dd");
    if (this.state.selectedDates.includes(dateStr)) {
      return ["events-bulk-active-day"];
    }
    return [];
  };

  handleSubmit = async () => {
    const { activeTab, selectedIds, max_reservable_days, selectedDates, mode } =
      this.state;
    const { store, onHide, onSuccess } = this.props;

    if (selectedIds.length === 0) {
      return MySwal.fire({
        icon: "warning",
        title: "No items selected",
        text: "Please select at least one item to update.",
      });
    }

    if (
      activeTab === "tables" &&
      max_reservable_days === "" &&
      selectedDates.length === 0
    ) {
      return MySwal.fire({
        icon: "warning",
        title: "No changes specified",
        text: "Please specify settings to update.",
      });
    }

    // For experiences, append mode with no dates is useless
    if (
      activeTab === "experiences" &&
      mode === "append" &&
      selectedDates.length === 0
    ) {
      return MySwal.fire({
        icon: "warning",
        title: "No dates selected",
        text: "Please select dates to append.",
      });
    }

    this.setState({ isSubmitting: true });

    try {
      let url = "";
      let payload = {};

      if (activeTab === "tables") {
        url = "/store/tables/bulk-update";
        payload = {
          tableIds: selectedIds,
          store_id: store ? store._id : undefined,
          mode: mode,
        };
        if (max_reservable_days !== "") {
          payload.max_reservable_days = parseInt(max_reservable_days, 10);
        }
        if (selectedDates.length > 0) {
          payload.blockout_dates = selectedDates;
        }
      } else {
        // Experiences
        url = "/item/experiences/bulk-update";
        payload = {
          experienceIds: selectedIds,
          available_days: selectedDates,
          mode: mode,
        };
      }

      const { data } = await fetchData("put", url, payload);

      if (data.result === "ok") {
        MySwal.fire({
          icon: "success",
          title: "Success",
          text: `Updated ${selectedIds.length} ${activeTab} successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
        if (onSuccess) onSuccess();
        onHide();
      } else if (data.result === "no updates needed") {
        MySwal.fire({
          icon: "info",
          title: "No Changes",
          text: "No updates were performed.",
        });
      }
    } catch (err) {
      console.error(err);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update. Please try again.",
      });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { show, onHide, tables = [], experiences = [] } = this.props;
    const {
      activeTab,
      selectedIds,
      max_reservable_days,
      selectedDates,
      mode,
      showCalendar,
      isSubmitting,
    } = this.state;

    const items = this.getItems();
    const hasBoth = tables.length > 0 && experiences.length > 0;

    // Dynamic Labels based on Tab
    const dateLabel =
      activeTab === "tables" ? "Blockout Dates" : "Available Dates";
    const dateHelpText =
      activeTab === "tables"
        ? "Selected dates will be unavailable for booking."
        : "Selected dates will be available for booking.";

    return (
      <>
        <Modal show={show} onHide={onHide} size="xl" centered>
          <style>{`
            .events-bulk-active-day { background-color: ${RESERVATION_COLOR} !important; opacity: 0.8; }
            .events-bulk-active-day .fc-daygrid-day-number { color: white; }
            .fc-button-primary {
              background-color: ${RESERVATION_COLOR} !important;
              border-color: ${RESERVATION_COLOR} !important;
            }
            .btn-reservation {
              background-color: ${RESERVATION_COLOR} !important;
              border-color: ${RESERVATION_COLOR} !important;
              color: white !important;
            }
            .btn-outline-reservation {
                color: ${RESERVATION_COLOR} !important;
                border-color: ${RESERVATION_COLOR} !important;
                background-color: transparent !important;
            }
            .btn-outline-reservation:hover {
                background-color: ${RESERVATION_COLOR} !important;
                color: white !important;
            }
            .text-reservation { color: ${RESERVATION_COLOR} !important; }
            /* Custom Checkbox */
            .custom-control-input:checked ~ .custom-control-label::before {
                background-color: ${RESERVATION_COLOR} !important;
                border-color: ${RESERVATION_COLOR} !important;
            }
            /* Radio */
            .radio-reservation > input:checked ~ span { border-color: ${RESERVATION_COLOR} !important; }
            .radio-reservation > input:checked ~ span::after { background-color: ${RESERVATION_COLOR} !important; }
            
            /* Tabs */
            .nav-tabs .nav-link.active {
                color: ${RESERVATION_COLOR};
                font-weight: bold;
                border-top: 3px solid ${RESERVATION_COLOR};
            }
            .nav-tabs .nav-link { color: #6c757d; }
          `}</style>

          <Modal.Header closeButton>
            <Modal.Title>Bulk Update Availability</Modal.Title>
          </Modal.Header>

          <Modal.Body
            style={{
              minHeight: "450px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Tabs if both data types exist */}
            {hasBoth && (
              <Nav
                variant="tabs"
                className="mb-4"
                activeKey={activeTab}
                onSelect={this.handleTabChange}
              >
                <Nav.Item>
                  <Nav.Link eventKey="tables">
                    Tables ({tables.length})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="experiences">
                    Experiences ({experiences.length})
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            )}

            <div className="row flex-grow-1">
              {/* Left Column: List Selection */}
              <div className="col-md-4 border-right d-flex flex-column">
                <h6 className="font-weight-bold mb-3">
                  1. Select {activeTab === "tables" ? "Tables" : "Experiences"}
                </h6>

                <div className="form-group mb-2">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="selectAllBulk"
                      checked={
                        items.length > 0 && selectedIds.length === items.length
                      }
                      onChange={this.handleSelectAll}
                    />
                    <label
                      className="custom-control-label"
                      htmlFor="selectAllBulk"
                    >
                      Select All ({items.length})
                    </label>
                  </div>
                </div>

                <div
                  className="flex-grow-1"
                  style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    border: "1px solid #ebedf3",
                    borderRadius: "4px",
                    padding: "10px",
                  }}
                >
                  {items.map((item) => {
                    const isChecked = selectedIds.includes(item._id);
                    return (
                      <div
                        key={item._id}
                        className="custom-control custom-checkbox mb-2"
                      >
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`bulk-item-${item._id}`}
                          checked={isChecked}
                          onChange={() => this.handleItemSelect(item._id)}
                        />
                        <label
                          className="custom-control-label"
                          htmlFor={`bulk-item-${item._id}`}
                          style={{ cursor: "pointer" }}
                        >
                          {item.name || item.title}
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 text-muted small">
                  {selectedIds.length} selected
                </div>
              </div>

              {/* Right Column: Settings */}
              <div className="col-md-8 pl-4">
                <h6 className="font-weight-bold mb-3">2. Configure Settings</h6>

                {/* Section: Max Reservable Days (Only for Tables) */}
                {activeTab === "tables" && (
                  <div className="card card-custom gutter-b bg-light-light mb-4 shadow-none border">
                    <div className="card-body py-4">
                      <Form.Group className="mb-0">
                        <Form.Label className="font-weight-bold">
                          Max Advance Booking Days
                          {InfoTooltip(
                            "Update the maximum advance booking days for selected tables.",
                          )}
                        </Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="number"
                            placeholder="e.g. 30"
                            value={max_reservable_days}
                            onChange={(e) =>
                              this.setState({
                                max_reservable_days: e.target.value,
                              })
                            }
                            min="1"
                            style={{ maxWidth: "200px" }}
                          />
                          <span className="text-muted ml-3 small">
                            Leave blank to keep unchanged.
                          </span>
                        </div>
                      </Form.Group>
                    </div>
                  </div>
                )}

                {/* Section: Calendar Dates */}
                <div className="card card-custom gutter-b bg-light-light mb-0 shadow-none border">
                  <div className="card-body py-4">
                    <Form.Group className="mb-0">
                      <Form.Label className="font-weight-bold">
                        {dateLabel}
                      </Form.Label>

                      {/* Mode Selection */}
                      <div className="mb-3">
                        <div className="radio-inline">
                          <label className="radio radio-outline radio-reservation">
                            <input
                              type="radio"
                              name="mode"
                              checked={mode === "replace"}
                              onChange={() =>
                                this.setState({ mode: "replace" })
                              }
                            />
                            <span></span>
                            Replace Existing
                          </label>
                          <label className="radio radio-outline radio-reservation ml-4">
                            <input
                              type="radio"
                              name="mode"
                              checked={mode === "append"}
                              onChange={() => this.setState({ mode: "append" })}
                            />
                            <span></span>
                            Append (Add)
                          </label>
                        </div>
                      </div>

                      {/* Date Display & Button */}
                      <div className="d-flex align-items-center justify-content-between bg-white border p-3 rounded">
                        <div>
                          {selectedDates.length > 0 ? (
                            <div className="d-flex flex-column">
                              <span className="text-reservation font-weight-bold mb-1">
                                {selectedDates.length} dates selected
                              </span>
                              <span className="text-muted small">
                                {formatConsecutiveDates(selectedDates.sort())}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted">
                              No dates selected
                            </span>
                          )}
                        </div>
                        <Button
                          variant="outline-secondary"
                          className="btn-outline-reservation"
                          onClick={() => this.setState({ showCalendar: true })}
                        >
                          {selectedDates.length > 0
                            ? "Edit Dates"
                            : "Select Dates"}
                        </Button>
                      </div>

                      <div className="mt-2 small text-muted">
                        <i className="fa fa-info-circle mr-1"></i>
                        {dateHelpText}
                      </div>
                    </Form.Group>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={onHide}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <Button
              variant="success"
              className="btn-reservation"
              onClick={this.handleSubmit}
              disabled={isSubmitting || selectedIds.length === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  Updating...
                </>
              ) : (
                "Apply Changes"
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Calendar Selection Modal */}
        <Modal
          show={showCalendar}
          onHide={() => this.setState({ showCalendar: false })}
          size="lg"
          centered
          onEntered={this.handleCalendarEntered}
        >
          <Modal.Header closeButton>
            <Modal.Title>Pick {dateLabel}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            <div className="p-4">
              <FullCalendar
                ref={this.calendarRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                selectable={true}
                dateClick={this.handleDateClick}
                dayCellClassNames={this.dayCellClassNames}
                headerToolbar={{
                  left: "prev,next",
                  center: "title",
                  right: "today",
                }}
                height="auto"
                validRange={{ start: new Date() }}
              />
            </div>
            <div className="px-4 pb-4">
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: 20,
                    height: 20,
                    backgroundColor: RESERVATION_COLOR,
                    opacity: 0.8,
                    marginRight: 8,
                    borderRadius: 4,
                  }}
                ></div>
                <span>Selected</span>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="success"
              className="btn-reservation"
              onClick={() => this.setState({ showCalendar: false })}
            >
              Done
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
}
